import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const PhaseActions = ({
  phase,
  userRole,
  onMarkComplete,
  onAdvancePhase,
  onOverride,
  isFinalPhase,
  onProgressUpdate,
  allPhases,
  projectLifecycleStatus
}) => {
  const [showOverrideModal, setShowOverrideModal] = React.useState(false);
  const [overrideReason, setOverrideReason] = React.useState('');
  const [showProgressModal, setShowProgressModal] = React.useState(false);
  const [progressValue, setProgressValue] = React.useState(phase?.progress || 0);

  const isDataLoaded = allPhases && allPhases?.length > 0;

  if (!isDataLoaded) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <h2 className="text-xl font-semibold text-foreground mb-6">Phase Actions</h2>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  // ============================================
  // 🔥 NEW LOGIC — PROGRESS IS THE ONLY TRUTH
  // ============================================

  const allPhasesPending = allPhases?.every(
    (p) => (p?.progress || 0) === 0
  );

  const isProjectCompleted = allPhases?.every(
    (p) => (p?.progress || 0) === 100
  );

  let phaseActionButtonText = '';
  let showPhaseActionButton = false;

  if (!isProjectCompleted) {
    if (allPhasesPending) {
      phaseActionButtonText = 'Start Project';
      showPhaseActionButton = true;
    } else if (isFinalPhase) {
      phaseActionButtonText = 'Complete Project';
      showPhaseActionButton = true;
    } else {
      phaseActionButtonText = 'Override Phase Status';
      showPhaseActionButton = true;
    }
  }

  // ============================================
  // END NEW LOGIC
  // ============================================

  const isPhaseOngoing = phase?.progress > 0 && phase?.progress < 100;
  const isPhaseCompleted = phase?.progress === 100;

  const handleOverride = () => {
    onOverride(overrideReason || 'No reason provided');
    setShowOverrideModal(false);
    setOverrideReason('');
  };

  const handleProgressUpdate = () => {
    if (onProgressUpdate) {
      onProgressUpdate(progressValue);
    }
    setShowProgressModal(false);
  };

  return (
    <>
      <div className="bg-card rounded-lg border border-border p-6">
        <h2 className="text-xl font-semibold text-foreground mb-6">Phase Actions</h2>

        <div className="space-y-3">
          {userRole === 'subcontractor' && isPhaseOngoing && !isProjectCompleted && (
            <>
              <button
                onClick={() => setShowProgressModal(true)}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-smooth min-h-[44px]"
              >
                <Icon name="TrendingUp" size={18} strokeWidth={2} />
                Update Progress
              </button>

              <button
                onClick={onMarkComplete}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-success text-success-foreground rounded-lg font-medium hover:bg-success/90 transition-smooth min-h-[44px]"
              >
                <Icon name="CheckCircle2" size={18} strokeWidth={2} />
                Mark Phase as Complete
              </button>
            </>
          )}

          {userRole === 'boss' && (
            <>
              {isPhaseCompleted && !isProjectCompleted && !allPhasesPending && (
                <button
                  onClick={onAdvancePhase}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-smooth min-h-[44px]"
                >
                  <Icon name="ArrowRight" size={18} strokeWidth={2} />
                  Advance to Next Phase
                </button>
              )}

              {showPhaseActionButton && (
                <button
                  onClick={() => setShowOverrideModal(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-warning text-warning-foreground rounded-lg font-medium hover:bg-warning/90 transition-smooth min-h-[44px]"
                >
                  <Icon name="Shield" size={18} strokeWidth={2} />
                  {phaseActionButtonText}
                </button>
              )}

              {isProjectCompleted && (
                <div className="p-4 bg-muted/50 rounded-lg text-center">
                  <p className="text-sm text-muted-foreground">
                    Project has been completed. No further actions available.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="mt-6 p-4 bg-muted/50 rounded-lg">
          <div className="flex items-start gap-3">
            <Icon name="Info" size={18} className="text-primary mt-0.5" strokeWidth={2} />
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground mb-1">Phase Guidelines</p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Update actual dates as work progresses</li>
                <li>• Upload progress photos regularly</li>
                <li>• Mark complete only when all work is finished</li>
                <li>• Notify boss of any delays immediately</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      {showOverrideModal && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-lg border border-border shadow-elevated max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">
                {phaseActionButtonText}
              </h3>
              <button
                onClick={() => setShowOverrideModal(false)}
                className="p-2 hover:bg-muted rounded-lg transition-smooth"
              >
                <Icon name="X" size={20} strokeWidth={2} />
              </button>
            </div>

            <p className="text-sm text-muted-foreground mb-4">
              {allPhasesPending
                ? 'You are about to start this project. This will activate the first phase and notify the assigned workers.'
                : isFinalPhase
                ? 'You are about to complete this project.'
                : 'You are about to override the current phase and move forward.'}
            </p>

            <div className="space-y-4">
              <textarea
                value={overrideReason}
                onChange={(e) => setOverrideReason(e?.target?.value)}
                placeholder="Add any comments..."
                rows={4}
                className="w-full px-4 py-3 bg-background border border-input rounded-lg"
              />

              <div className="flex gap-2">
                <button
                  onClick={handleOverride}
                  className="flex-1 px-4 py-2 bg-warning text-warning-foreground rounded-lg"
                >
                  {phaseActionButtonText}
                </button>
                <button
                  onClick={() => setShowOverrideModal(false)}
                  className="flex-1 px-4 py-2 bg-muted rounded-lg"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showProgressModal && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-lg border border-border shadow-elevated max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Update Phase Progress</h3>

            <input
              type="range"
              min="0"
              max="100"
              value={progressValue}
              onChange={(e) => setProgressValue(parseInt(e?.target?.value))}
              className="w-full"
            />

            <div className="flex gap-2 mt-4">
              <button
                onClick={handleProgressUpdate}
                className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg"
              >
                Update
              </button>
              <button
                onClick={() => setShowProgressModal(false)}
                className="flex-1 px-4 py-2 bg-muted rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PhaseActions;